import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { track } from '../lib/analytics';

const AuthContext = createContext(null);

// PostHog helpers — no-op in preview/localhost where the snippet is not loaded.
function phIdentify(user) {
  if (!user) return;
  try {
    const id = user.id || user._id || user.email;
    const props = {
      email: user.email,
      name: user.name,
      role: user.role,
      subscription_tier: user.subscription_tier,
      onboarding_completed: user.onboarding_completed,
    };
    // PostHog loads deferred (after page load) — remember the identity so the
    // bootstrap in index.html can replay it once the snippet is ready.
    window.__cmxPendingIdentify = { id, props };
    if (window.posthog && typeof window.posthog.identify === 'function') {
      window.posthog.identify(id, props);
    }
  } catch (_) { /* ignore analytics errors */ }
}
function phReset() {
  try {
    if (window.posthog && typeof window.posthog.reset === 'function') {
      window.posthog.reset();
    }
  } catch (_) { /* ignore analytics errors */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshIntervalRef = useRef(null);

  // On mount: load from localStorage immediately, then refresh from backend
  useEffect(() => {
    const token = localStorage.getItem('token');
    const stored = localStorage.getItem('user');

    if (token && stored) {
      try {
        setUser(JSON.parse(stored));
      } catch { /* ignore */ }

      // Refresh from backend for latest data (subscription, name, etc.)
      api.get('/api/auth/me')
        .then(({ data }) => {
          localStorage.setItem('user', JSON.stringify(data));
          setUser(data);
          phIdentify(data);
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Auto-refresh the JWT every 24h (and on load when older than 24h) so an
  // active user never hits token expiry mid-session (backend tokens default
  // to 72h — the old 6-day cadence let tokens die before refresh).
  useEffect(() => {
    if (!user) {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      return;
    }
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    refreshIntervalRef.current = setInterval(() => {
      api.post('/api/auth/refresh')
        .then(({ data }) => {
          if (data.token) {
            localStorage.setItem('token', data.token);
          }
        })
        .catch(() => { /* token refresh failed, will expire naturally */ });
    }, ONE_DAY_MS);

    // Also refresh on first load if token is older than 24h
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const issuedAt = payload.iat * 1000;
        const age = Date.now() - issuedAt;
        if (age > ONE_DAY_MS) {
          api.post('/api/auth/refresh').then(({ data }) => {
            if (data.token) localStorage.setItem('token', data.token);
          }).catch(() => {});
        }
      } catch { /* ignore parse errors */ }
    }

    return () => { if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current); };
  }, [user]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/api/auth/login', { email, password });
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    phIdentify(data.user);
    return data.user;
  }, []);

  const applyAuth = useCallback((data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    phIdentify(data.user);
  }, []);

  const signup = useCallback(async (email, password, name) => {
    const refCode = localStorage.getItem('ref_code');
    const payload = { email, password, name };
    if (refCode) payload.ref_code = refCode;

    const { data } = await api.post('/api/auth/signup', payload);
    track('sign_up', { method: 'email' });
    if (refCode) {
      localStorage.removeItem('ref_code');
      localStorage.removeItem('ref_code_at');
    }

    // Email OTP verification gate — no token until the code is confirmed.
    if (data.verification_required) return data;

    applyAuth(data);
    return data.user;
  }, [applyAuth]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    phReset();
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/api/auth/me');
      localStorage.setItem('user', JSON.stringify(data));
      setUser(data);
      phIdentify(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  // Backend said TRIAL_LOCKED (trial ran out mid-session) → reload the profile so the lock wall renders.
  useEffect(() => {
    const onLocked = () => { refreshUser(); };
    window.addEventListener('cmx:trial-locked', onLocked);
    return () => window.removeEventListener('cmx:trial-locked', onLocked);
  }, [refreshUser]);

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, applyAuth, logout, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
